<!-- ── Hero ─────────────────────────────────────────── -->
<section class="hero">
  <div class="hero-bg" style="background-image: url('assets/images/Home.jpg');"></div>
  <div class="hero-overlay"></div>
  <div class="container hero-content">
    <div class="hero-inner">
      <div class="hero-left" [class.hero-left-animate]="slideChanging">
        <div class="hero-pill">
          <span class="hero-pill-dot"></span>
          {{ activeSlide.date }}
        </div>
        <h1 class="hero-headline" [innerHTML]="activeSlide.title"></h1>
        <p class="hero-sub">{{ activeSlide.sub }}</p>
        <div class="hero-ctas">
          <a routerLink="/documentation" class="btn-hero-primary">📄 Nos documentations</a>
          <a routerLink="/contact" class="btn-hero-outline">Nous contacter →</a>
        </div>
      </div>
      <div class="hero-right">
        <div class="hero-image-wrap" [class.hero-image-animate]="slideChanging">
          <img
            class="hero-main-img"
            [src]="activeSlide.image"
            [alt]="activeSlide.title | stripHtml"
            loading="eager"
          />
          <div class="hero-indicators">
            <div class="hero-indicator-dots">
              @for (slide of slides; track slide.date; let i = $index) {
                <button
                  type="button"
                  class="hero-dot"
                  [class.is-active]="activeIndex === i"
                  (click)="setSlide(i)"
                  aria-label="Aller au slide {{ i + 1 }}"
                ></button>
              }
            </div>
            <span class="hero-indicator-count">
              {{ activeIndex + 1 }} / {{ slides.length }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── Stats ─────────────────────────────────────────── -->
<div class="stats-bar-wrap" appScrollIn>
  <div class="container">
    <div class="stats-bar">
      @for (stat of stats; track stat.label) {
        <div class="stat-item">
          <span class="stat-val">{{ stat.value }}</span>
          <span class="stat-lbl">{{ stat.label }}</span>
          <div class="stat-divider"></div>
        </div>
      }
    </div>
  </div>
</div>

<!-- ── Portal Access Banner ──────────────────────────── -->
<div class="portal-banner" appScrollIn>
  <div class="container">
    <div class="portal-banner-inner">
      <div class="portal-banner-left">
        <div class="portal-banner-icon">🔐</div>
        <div>
          <p class="portal-banner-label">Accès à la plateforme LOGIRAD</p>
          <p class="portal-banner-url">https://logirad.dgrad.cd</p>
        </div>
      </div>
      <div class="portal-banner-right">
        <p class="portal-banner-hint">Navigateurs compatibles : Chrome, Firefox, Edge, Safari</p>
        <a href="https://logirad.dgrad.cd" target="_blank" class="portal-banner-btn">
          Se connecter →
        </a>
      </div>
    </div>
  </div>
</div>

<!-- ── What LOGIRAD Is ───────────────────────────────── -->
<section class="section offer" id="presentation" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Présentation</div>
      <h2 class="section-title">UNE CHAÎNE NUMÉRISÉE DE BOUT EN BOUT</h2>
      <p class="section-sub">
        LOGIRAD est le logiciel de gestion des recettes non fiscales de la DGRAD permettant la constatation,
        la liquidation, l'ordonnancement, le recouvrement, le contrôle, la gestion des exonérations et des
        contentieux ainsi que l'édition des tableaux de bord. Rendu obligatoire par le décret n°22/18 du 4 mai 2022.
      </p>
    </div>
    <div class="offer-grid">
      @for (card of offerCards; track card.title) {
        <div class="offer-card">
          <div class="offer-thumb">
            <div class="offer-thumb-icon">{{ card.icon }}</div>
          </div>
          <h3>{{ card.title }}</h3>
          <p>{{ card.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- ── 5 Application Modules ─────────────────────────── -->
<section class="section app-modules-section" id="app-modules" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Architecture logicielle</div>
      <h2 class="section-title">LES 5 MODULES DE L'APPLICATION</h2>
      <p class="section-sub">
        LOGIRAD est structurée en cinq modules complémentaires couvrant l'ensemble du cycle de gestion,
        de l'administration jusqu'aux échanges avec les systèmes partenaires.
      </p>
    </div>
    <div class="app-modules-grid">
      @for (mod of appModules; track mod.num) {
        <div class="app-module-card">
          <div class="app-module-num">{{ mod.num }}</div>
          <div class="app-module-icon">{{ mod.icon }}</div>
          <h4>{{ mod.title }}</h4>
          <p>{{ mod.desc }}</p>
          @if (mod.partners && mod.partners.length > 0) {
            <div class="app-module-partners">
              @for (p of mod.partners; track p) {
                <span class="app-module-partner-chip">{{ p }}</span>
              }
            </div>
          }
        </div>
      }
    </div>
  </div>
</section>

<!-- ── Dashboard Overview ────────────────────────────── -->
<section class="section dashboard-section" id="tableau-bord" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Interface utilisateur</div>
      <h2 class="section-title">TABLEAU DE BORD & NAVIGATION</h2>
      <p class="section-sub">
        La page d'accueil de LOGIRAD est un tableau de bord personnalisé selon le profil utilisateur,
        offrant un accès en un clic à tous les modules applicatifs.
      </p>
    </div>
    <div class="dashboard-grid">
      @for (panel of dashboardPanels; track panel.title) {
        <div class="dashboard-panel">
          <div class="dashboard-panel-icon">{{ panel.icon }}</div>
          <div>
            <h4>{{ panel.title }}</h4>
            <p>{{ panel.desc }}</p>
          </div>
        </div>
      }
    </div>
    <div class="dashboard-note">
      <span>⚠️</span>
      <p>La barre de menu affiche votre <strong>Ministère</strong>, <strong>Service d'assiette</strong> et
        <strong>Centre d'ordonnancement</strong>. Si ces informations ne correspondent pas à votre service,
        contactez immédiatement l'administrateur LOGIRAD.</p>
    </div>
  </div>
</section>

<!-- ── User Profiles ─────────────────────────────────── -->
<section class="section profiles-section" id="profils" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Gestion des accès</div>
      <h2 class="section-title">PROFILS UTILISATEURS LOGIRAD</h2>
      <p class="section-sub">
        Chaque utilisateur dispose d'un profil défini par l'administrateur système avec des droits
        d'accès correspondant à son rôle dans la chaîne des recettes.
      </p>
    </div>
    <div class="profiles-grid">
      @for (profile of userProfiles; track profile.name) {
        <div class="profile-card">
          <div class="profile-icon-wrap">{{ profile.icon }}</div>
          <div class="profile-content">
            <h4>{{ profile.name }}</h4>
            <p>{{ profile.description }}</p>
          </div>
        </div>
      }
    </div>
    <div class="profiles-note">
      <span class="note-icon">ℹ️</span>
      <p>Les identifiants (adresse e-mail et mot de passe) sont fournis par l'administrateur LOGIRAD à la DGRAD.
        Chaque utilisateur est tenu de garder ses identifiants confidentiels et de ne jamais les partager.</p>
    </div>
  </div>
</section>

<!-- ── Functional Modules (4-per-row cards) ──────────── -->
<section class="section services-section" id="modules" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Modules fonctionnels</div>
      <h2 class="section-title">SERVICES INTÉGRÉS LOGIRAD</h2>
      <p class="section-sub">
        LOGIRAD centralise les principaux modules de gestion : assujettis, déclarations, dossiers,
        taxation, ordonnancement, contrôle, recouvrement et contentieux.
      </p>
    </div>
    <div class="services-grid">
      @for (svc of serviceCards; track svc.title) {
        <div class="svc-card">
          <div class="svc-card-header">
            <div class="svc-icon-wrap">{{ svc.icon }}</div>
            <h4>{{ svc.title }}</h4>
          </div>
          <ul>
            @for (item of svc.items; track item) {
              <li>{{ item }}</li>
            }
          </ul>
        </div>
      }
    </div>
  </div>
</section>

<!-- ── Taxation Workflow ──────────────────────────────── -->
<section class="section taxation-section" id="taxation" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Processus de taxation</div>
      <h2 class="section-title">TYPES DE TAXATIONS & WORKFLOW</h2>
      <p class="section-sub">
        LOGIRAD gère quatre types de taxations distincts. Chaque note de taxation suit un circuit
        de validation avant d'être transmise à l'ordonnateur.
      </p>
    </div>

    <!-- 4 taxation types -->
    <div class="tax-types-grid">
      @for (tt of taxationTypes; track tt.label) {
        <div class="tax-type-card">
          <div class="tax-type-header">
            <div class="tax-type-icon">{{ tt.icon }}</div>
            <h4>{{ tt.label }}</h4>
          </div>
          <p>{{ tt.desc }}</p>
        </div>
      }
    </div>

    <!-- Taxation 4-step workflow -->
    <div class="tax-workflow">
      <div class="tax-workflow-title">
        <span>⚙️</span> Étapes de création d'une taxation (Taxateur)
      </div>
      <div class="tax-workflow-steps">
        @for (step of taxationWorkflow; track step.num; let last = $last) {
          <div class="tax-wf-step">
            <div class="tax-wf-inner">
              <div class="tax-wf-num">{{ step.num }}</div>
              <p class="tax-wf-label">{{ step.label }}</p>
            </div>
            @if (!last) {
              <div class="tax-wf-arrow">→</div>
            }
          </div>
        }
      </div>
    </div>

    <!-- Chef-taxateur validation note -->
    <div class="tax-validation-note">
      <div class="tvn-icon">✅</div>
      <div>
        <strong>Validation par le Chef-taxateur</strong>
        <p>Seul le profil Chef-taxateur peut valider ou rejeter les notes de taxation de son service ou centre
          d'ordonnancement. Il sélectionne son avis (Valider / Rejeter), renseigne une observation et clôture la note.
          La note validée passe automatiquement en attente d'ordonnancement.</p>
      </div>
    </div>
  </div>
</section>

<!-- ── Ordonnancement Workflow ────────────────────────── -->
<section class="section ordonnancement-section" id="ordonnancement" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Ordonnancement</div>
      <h2 class="section-title">GESTION DES ORDONNANCEMENTS</h2>
      <p class="section-sub">
        L'ordonnateur autorise les notes de taxation validées et produit les notes de perception (bons à payer)
        transmises aux assujettis pour le paiement.
      </p>
    </div>
    <div class="ordo-layout">
      <!-- 3-step workflow -->
      <div class="ordo-workflow">
        @for (step of ordonancementSteps; track step.num; let last = $last) {
          <div class="ordo-step">
            <div class="ordo-step-inner">
              <div class="ordo-step-num">{{ step.num }}</div>
              <div class="ordo-step-text">
                <div class="ordo-step-title-row">
                  <div class="ordo-step-icon">{{ step.icon }}</div>
                  <h4>{{ step.title }}</h4>
                </div>
                <p>{{ step.desc }}</p>
              </div>
            </div>
            @if (!last) {
              <div class="ordo-step-arrow">
                <svg viewBox="0 0 24 56" fill="none">
                  <line x1="12" y1="0" x2="12" y2="36" stroke="#c9a84c" stroke-width="2.5" stroke-dasharray="6 4"/>
                  <path d="M3 33 L12 52 L21 33 Z" fill="#c9a84c"/>
                </svg>
              </div>
            }
          </div>
        }
      </div>

      <!-- Registres panel -->
      <div class="ordo-registers">
        <div class="ordo-registers-title">📋 Registres disponibles</div>
        @for (reg of ordonancementRegistres; track reg.label) {
          <div class="ordo-reg-row">
            <div class="ordo-reg-icon">{{ reg.icon }}</div>
            <div>
              <strong>{{ reg.label }}</strong>
              <span>{{ reg.desc }}</span>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</section>

<!-- ── Security & Connection ─────────────────────────── -->
<section class="section security-section" id="securite" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Accès sécurisé</div>
      <h2 class="section-title">CONNEXION & SÉCURITÉ</h2>
      <p class="section-sub">
        L'accès à LOGIRAD est limité aux agents autorisés. Chaque utilisateur est responsable de la
        confidentialité de ses identifiants.
      </p>
    </div>
    <div class="security-grid">
      <!-- Login mock -->
      <div class="security-login-card">
        <div class="login-mock">
          <div class="login-mock-bar">
            <div class="login-mock-dots">
              <span></span><span></span><span></span>
            </div>
            <div class="login-mock-url">🔒 www.logirad.dgrad.cd</div>
          </div>
          <div class="login-mock-body">
            <div class="login-mock-logo">dgrad<strong>.gouv.cd</strong></div>
            <p class="login-mock-tagline">La plateforme de gestion des recettes de la DGRAD</p>
            <p class="login-mock-welcome">Bienvenue au portail LOGIRAD</p>
            <div class="login-mock-field">
              <span>👤</span>
              <span>nom&#64;logirad.cd</span>
            </div>
            <div class="login-mock-field">
              <span>🔑</span>
              <span>••••••••</span>
            </div>
            <div class="login-mock-btn">→ SE CONNECTER</div>
            <p class="login-mock-forgot">Cliquez ici si vous avez oublié votre mot de passe ?</p>
          </div>
        </div>
      </div>

      <!-- Security rules + change password -->
      <div class="security-rules">
        @for (rule of securityRules; track rule.title) {
          <div class="security-rule-card">
            <div class="security-rule-header">
              <div class="security-rule-icon">{{ rule.icon }}</div>
              <strong>{{ rule.title }}</strong>
            </div>
            <p>{{ rule.desc }}</p>
          </div>
        }
        <div class="security-change-pw">
          <div class="security-change-icon">🔄</div>
          <div>
            <strong>Modifier son mot de passe</strong>
            <p>Cliquez sur votre nom en haut à gauche de la barre horizontale →
              <em>[Modifier votre mot de passe]</em> → saisissez l'ancien mot de passe,
              puis le nouveau à deux reprises → cliquez sur <em>[Modifier]</em> pour valider.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── ISYS-Régies interoperability ───────────────────── -->
<section class="section isys-section" appScrollIn>
  <div class="container">
    <div class="isys-card">
      <div class="isys-left">
        <div class="isys-kicker">Encadré — Interopérabilité</div>
        <h2>C'est quoi ISYS‑Régies ?</h2>
        <p>
          Opérationnel sur toute l'étendue de la RDC depuis janvier 2022 (plus de 6 000 utilisateurs formés),
          ISYS‑Régies automatise la procédure de perception des recettes, dès l'encaissement par les
          intervenants financiers (banques, établissements de crédit), jusqu'au reversement à la Banque
          Centrale du Congo et à la comptabilisation dans le compte général du Trésor.
        </p>
        <p>
          En plus de la dématérialisation de la procédure d'apurement, il apporte transparence et fiabilité
          sur les recettes et la trésorerie de l'État. LOGIRAD s'interface avec ISYS‑Régies via son
          module d'échanges de données (API).
        </p>
        <div class="isys-badges">
          <span>✔ Encaissement automatisé</span>
          <span>✔ Reversement BCC</span>
          <span>✔ Apurement dématérialisé</span>
          <span>✔ +6 000 utilisateurs formés</span>
          <span>✔ Interfacé avec LOGIRAD (API)</span>
        </div>
      </div>
      <div class="isys-right">
        <div class="isys-flow">
          @for (step of isysFlow; track step.label; let last = $last) {
            <div class="isys-step">
              <div class="isys-step-icon">{{ step.icon }}</div>
              <div class="isys-step-label">{{ step.label }}</div>
            </div>
            @if (!last) {
              <div class="isys-arrow">↓</div>
            }
          }
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── National deployment ─── -->
<section class="section extension" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Déploiement national</div>
      <h2 class="section-title">Extension de la procédure dématérialisée LOGIRAD</h2>
      <p class="section-sub">
        Rendue obligatoire par décret, la mise en production a démarré au 1er janvier 2023 pour 5 services
        d'assiette sur 51, visant 1 300 utilisateurs. Le déploiement s'étend progressivement aux 26 provinces.
      </p>
    </div>
    <div class="ext-grid">
      <div class="ext-map-wrap">
        <div class="ext-map-card">
          <div class="map-inner">
            <svg viewBox="0 0 300 280" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M150,20 L210,35 L260,60 L275,110 L265,160 L250,200
                   L220,240 L175,260 L130,255 L85,240 L50,210 L30,170
                   L25,130 L40,85 L70,50 L110,28 Z"
                fill="rgba(201,168,76,.12)"
                stroke="rgba(201,168,76,.55)"
                stroke-width="1.5"
              />
              <circle cx="148" cy="90"  r="4" fill="#c9a84c" opacity=".9"/>
              <circle cx="175" cy="120" r="3" fill="#c9a84c" opacity=".7"/>
              <circle cx="120" cy="130" r="3" fill="#c9a84c" opacity=".7"/>
              <circle cx="155" cy="155" r="3" fill="#c9a84c" opacity=".7"/>
              <circle cx="190" cy="145" r="3" fill="#c9a84c" opacity=".7"/>
              <circle cx="130" cy="170" r="3" fill="#c9a84c" opacity=".7"/>
              <circle cx="165" cy="185" r="3" fill="#c9a84c" opacity=".7"/>
              <circle cx="100" cy="155" r="3" fill="rgba(201,168,76,.4)"/>
              <circle cx="200" cy="170" r="3" fill="rgba(201,168,76,.4)"/>
              <circle cx="80"  cy="120" r="3" fill="rgba(201,168,76,.4)"/>
              <text x="150" y="225" fill="rgba(255,255,255,.3)" font-size="9"
                    text-anchor="middle" font-family="sans-serif">
                République Démocratique du Congo
              </text>
            </svg>
            <div class="map-badge">Déploiement national en cours</div>
            <div class="ext-map-pills">
              <div class="ext-map-pill">
                <span class="pill-dot"></span>
                <div>
                  <p class="pill-title">Procédure dématérialisée</p>
                  <p class="pill-sub">Notes d'imposition, avis de paiement et recouvrement gérés en ligne.</p>
                </div>
              </div>
              <div class="ext-map-pill">
                <span class="pill-dot"></span>
                <div>
                  <p class="pill-title">Couverture nationale progressive</p>
                  <p class="pill-sub">Déploiement par vagues dans l'ensemble des 26 provinces.</p>
                </div>
              </div>
              <div class="ext-map-pill">
                <span class="pill-dot"></span>
                <div>
                  <p class="pill-title">Interopérabilité avec ISYS‑Régies</p>
                  <p class="pill-sub">Chaîne informatisée des recettes, de l'encaissement à la trésorerie.</p>
                </div>
              </div>
            </div>
            <p class="ext-note">
              <strong>Formation continue :</strong> Plus de 516 agents formés en 2024,
              incluant 157 pour le Kasaï Oriental/Lomami/Sankuru à Mbuji-Mayi.
            </p>
          </div>
        </div>
      </div>
      <div class="ext-right">
        <div class="ext-info-card">
          <div class="ext-info-grid">
            <div class="ext-info-col">
              <div class="ext-services-label"><span></span> Services d'assiette</div>
              <div class="ext-chips">
                @for (chip of assietteServices; track chip) {
                  <span class="ext-chip">{{ chip }}</span>
                }
              </div>
            </div>
            <div class="ext-info-col">
              <div class="ext-services-label"><span></span> Provinces couvertes</div>
              <div class="ext-phases">
                @for (phase of phases; track phase.label) {
                  <div class="ext-phase">
                    <div class="ext-phase-label">{{ phase.label }}</div>
                    <p>{{ phase.provinces }}</p>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── Newsletter ─────────────────────────────────────── -->
<section class="newsletter section" id="gallery" appScrollIn>
  <div class="container">
    <div class="section-header-center">
      <div class="section-kicker">Actualités</div>
      <h2 class="section-title">NEWSLETTER</h2>
    </div>
    <div class="news-grid">
      @for (item of newsItems; track item.title) {
        <div class="news-card">
          <div class="news-img" [style.background]="item.bg">
            <span>{{ item.icon }}</span>
          </div>
          <div class="news-body">
            <div class="news-meta">
              <span class="news-type">{{ item.type }}</span>
              <span class="news-date">{{ item.date }}</span>
            </div>
            <h3>{{ item.title }}</h3>
          </div>
        </div>
      }
    </div>
    <div class="newsletter-cta">
      <button class="btn-more">Voir plus d'actualités →</button>
    </div>
  </div>
</section>